import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Activity } from 'lucide-react';
import { useOutletContext } from 'react-router-dom';
import { useSuperadminAuth } from '../../context/SuperadminAuthContext';
import {
  ErrorState,
  Field,
  KeyValueList,
  LoadingState,
  MetricCard,
  SectionCard,
  SelectInput,
  formatNumber,
} from '../../components/superadmin/SuperAdminUI';

const ShopUsageSummary = () => {
  const { shop, shopId } = useOutletContext();
  const { getShopUsageSummary } = useSuperadminAuth();
  const [range, setRange] = useState('month');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [usageSummary, setUsageSummary] = useState(null);

  const loadUsage = useCallback(async (nextRange = range) => {
    try {
      setLoading(true);
      setError('');
      const response = await getShopUsageSummary(shopId, { range: nextRange });
      if (!response?.ok) {
        throw new Error('Failed to load usage summary');
      }
      setUsageSummary(response.data || null);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load usage summary');
    } finally {
      setLoading(false);
    }
  }, [getShopUsageSummary, range, shopId]);

  useEffect(() => {
    loadUsage(range);
  }, [loadUsage, range]);

  const metricCards = useMemo(() => ([
    {
      label: 'Bookings',
      value: formatNumber(usageSummary?.summary?.bookings?.total),
      icon: <Activity className="h-5 w-5" />,
    },
    {
      label: 'Completed scans',
      value: formatNumber(usageSummary?.summary?.ai?.completed_scans),
      icon: <Activity className="h-5 w-5" />,
    },
    {
      label: 'Credits used',
      value: formatNumber(usageSummary?.summary?.ai?.credits_used_total),
      icon: <Activity className="h-5 w-5" />,
    },
    {
      label: 'New clients',
      value: formatNumber(usageSummary?.summary?.clients?.new_total),
      icon: <Activity className="h-5 w-5" />,
    },
  ]), [usageSummary]);

  return (
    <div className="space-y-6">
      {error ? <ErrorState error={error} onRetry={() => loadUsage(range)} /> : null}

      <SectionCard
        title="Usage summary"
        subtitle={`Read-only usage metrics for ${shop?.name || `Shop #${shopId}`}`}
        icon={<Activity className="h-5 w-5" style={{ color: '#7d6633' }} />}
        action={(
          <div className="w-40">
            <Field label="Range">
              <SelectInput value={range} onChange={(event) => setRange(event.target.value)}>
                <option value="day">Day</option>
                <option value="week">Week</option>
                <option value="month">Month</option>
              </SelectInput>
            </Field>
          </div>
        )}
      >
        {loading ? (
          <LoadingState lines={6} />
        ) : (
          <div className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {metricCards.map((card) => (
                <MetricCard key={card.label} {...card} />
              ))}
            </div>

            <KeyValueList
              items={[
                { label: 'Bookings completed', value: formatNumber(usageSummary?.summary?.bookings?.by_status?.completed) },
                { label: 'Bookings cancelled', value: formatNumber(usageSummary?.summary?.bookings?.by_status?.cancelled) },
                { label: 'Active clients', value: formatNumber(usageSummary?.summary?.clients?.active_total) },
                { label: 'Completed scans', value: formatNumber(usageSummary?.summary?.ai?.completed_scans) },
                { label: 'Face shapes detected', value: formatNumber(usageSummary?.summary?.ai?.face_shape_detected_total) },
                { label: 'Manual credits granted', value: formatNumber(usageSummary?.summary?.credits?.manual_credits_granted_total) },
              ]}
            />
          </div>
        )}
      </SectionCard>
    </div>
  );
};

export default ShopUsageSummary;
